import { useEffect, useState } from "react"
import type { ButtonGroupItem, ButtonGroupProps } from "./ButtonGroup.types"

type UseButtonGroupProps = Pick<
  ButtonGroupProps,
  | "items"
  | "defaultValue"
  | "value"
  | "onSelectionChange"
  | "multiple"
  | "mandatory"
>

export const useButtonGroup = ({
  items,
  defaultValue,
  value,
  onSelectionChange,
  multiple = false,
  mandatory = false,
}: UseButtonGroupProps) => {
  const [internalValue, setInternalValue] = useState<
    string | string[] | undefined
  >(defaultValue ?? (multiple ? [] : undefined))

  const isControlled = value !== undefined
  const currentValue = isControlled ? value : internalValue

  useEffect(() => {
    if (mandatory && items.length > 0 && !isControlled) {
      if (multiple) {
        if (!Array.isArray(internalValue) || internalValue.length === 0) {
          setInternalValue([items[0].value])
        }
      } else if (internalValue === undefined) {
        setInternalValue(items[0].value)
      }
    }
  }, [items, mandatory, multiple, internalValue, isControlled])

  const handleSelect = (item: ButtonGroupItem) => {
    let newValue: string | string[] | undefined

    if (multiple) {
      const currentValues = (currentValue as string[]) || []
      if (currentValues.includes(item.value)) {
        newValue = currentValues.filter(v => v !== item.value)
        if (mandatory && newValue.length === 0) {
          newValue = currentValue
        }
      } else {
        newValue = [...currentValues, item.value]
      }
    } else {
      if (mandatory && currentValue === item.value) {
        newValue = currentValue
      } else {
        newValue = currentValue === item.value ? undefined : item.value
      }
    }

    if (!isControlled) {
      setInternalValue(newValue)
    }

    if (onSelectionChange) {
      onSelectionChange(newValue || (multiple ? [] : ""))
    }
  }

  const isSelected = (item: ButtonGroupItem) => {
    if (multiple && Array.isArray(currentValue)) {
      return currentValue.includes(item.value)
    }
    return currentValue === item.value
  }

  return {
    handleSelect,
    isSelected,
  }
}
